import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getMentoringContent } from '../features/mentoring/mentoringContent';
import sitemapRoutes from '../generated/sitemapRoutes.json';

const Sitemap = () => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language.startsWith('en') ? 'en' : 'fr';

    const hostTitles = {
        '/': t('header.nav.home'),
        '/roadmap': locale === 'fr' ? 'Feuille de route' : 'Roadmap',
        '/talents': t('talents.navLabel'),
        '/contact': t('header.nav.contact'),
        '/pro-email': t('proEmail.navLabel'),
        '/privacy-policy': t('footer.privacy'),
        '/terms-of-service': t('footer.terms'),
        '/cookies-policy': t('legal.cookies.title'),
        '/sitemap': t('footer.sitemap'),
    };
    const fallbackTitle = (route) => route.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || route;
    const links = sitemapRoutes.filter((entry) => entry.section === 'site').map((entry) => ({
        path: entry.path,
        title: hostTitles[entry.path] || fallbackTitle(entry.path),
    }));
    const homeSections = [
        { path: '/#services', title: t('header.nav.services') },
        { path: '/#industries', title: t('header.nav.industries') },
        { path: '/#values', title: t('header.nav.values') },
        { path: '/#contact', title: t('header.nav.contact') },
    ];
    const trainingLinks = sitemapRoutes
        .filter((entry) => entry.section === 'training' && entry.locale === locale)
        .map((entry) => ({
            path: entry.path,
            title: entry.pageKey === 'catalogue'
                ? t('training.sitemapSection')
                : t(`training.offers.${entry.pageKey}`, { defaultValue: fallbackTitle(entry.path) }),
        }));
    const mentoring = getMentoringContent(locale);
    const mentoringLinks = sitemapRoutes
        .filter((entry) => entry.section === 'mentoring' && entry.locale === locale)
        .map((entry) => ({
            path: entry.path,
            title: entry.pageKey === 'index' ? mentoring.nav.brand : (
                mentoring.nav.links.find((link) => link.pageKey === entry.pageKey)?.label
                || fallbackTitle(entry.path)
            ),
        }));

    return (
        <div className="bg-gray-50 min-h-screen py-20">
            <SEO
                path="/sitemap"
                title={`${t('footer.sitemap')} | CSL Brands`}
                description="Browse all pages of the CSL Brands website."
                keywords="CSL Brands sitemap, plan du site, site map, all pages"
            />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-12 text-center">{t('footer.sitemap')}</h1>

                <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                    <ul className="space-y-6">
                        {links.map((link, index) => (
                            <li key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                                <Link
                                    to={link.path}
                                    className="text-xl text-gray-700 hover:text-csl-green transition-colors duration-300 flex items-center group"
                                >
                                    <span className="w-2 h-2 bg-csl-green rounded-full mr-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    {link.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('header.nav.home')}</h2>
                    <ul className="space-y-6">
                        {homeSections.map((link) => (
                            <li key={link.path} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                                <Link to={link.path} className="text-lg text-gray-700 hover:text-csl-green transition-colors duration-300 flex items-center group">
                                    <span className="w-2 h-2 bg-csl-green rounded-full mr-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    {link.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('training.sitemapSection')}</h2>
                    <ul className="space-y-6">
                        {trainingLinks.map((link, index) => (
                            <li key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                                <Link
                                    to={link.path}
                                    className="text-lg text-gray-700 hover:text-csl-green transition-colors duration-300 flex items-center group"
                                >
                                    <span className="w-2 h-2 bg-csl-green rounded-full mr-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    {link.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8 mt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">{mentoring.nav.brand}</h2>
                    <ul className="space-y-6">
                        {mentoringLinks.map((link) => (
                            <li key={link.path} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                                <Link
                                    to={link.path}
                                    className="text-lg text-gray-700 hover:text-csl-green transition-colors duration-300 flex items-center group"
                                >
                                    <span className="w-2 h-2 bg-csl-green rounded-full mr-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    {link.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Sitemap;
